// src/modules/hrm/tasks/components/tables/TaskMainCompletedTable.jsx
import React, { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCheckCircle,
  faLock,
  faImage,
  faFilePdf,
  faXmarkCircle,
  faPaperclip,
  faChevronRight,
} from "@fortawesome/free-solid-svg-icons";
import Pagination from "../../../../../components/common/Pagination";
import {
  ZoomIn,
  ZoomOut,
  RotateCcw,
  RefreshCw,
  X,
} from "lucide-react";
import { formatDateUS } from "../../../../../utils/formatUtils";

const getStatusConfig = (status) => {
  if (status === "rejected") {
    return {
      text: "Không duyệt",
      color: "bg-slate-100 text-slate-500 border-slate-200",
      ribbon: "bg-slate-500",
      icon: faLock,
    };
  }
  if (status === "rejected-report") {
    return {
      text: "Chưa đạt",
      color: "bg-rose-50 text-rose-600 border-rose-100",
      ribbon: "bg-rose-500",
      icon: faXmarkCircle,
    };
  }
  return {
    text: "Hoàn thành",
    color: "bg-emerald-50 text-emerald-600 border-emerald-100",
    ribbon: "bg-emerald-500",
    icon: faCheckCircle,
  };
};

const getScoreClass = (point) => {
  if (point >= 5) return "bg-emerald-50 text-emerald-600 border-emerald-100";
  if (point >= 3) return "bg-amber-50 text-amber-600 border-amber-100";
  return "bg-rose-50 text-rose-600 border-rose-100";
};

const getInitial = (name) =>
  name ? name.trim().split(" ").pop().charAt(0).toUpperCase() : "?";

const TaskMainTable = ({ tasks = [], onSelect }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [previewImage, setPreviewImage] = useState(null);
  const [previewPdf, setPreviewPdf] = useState(null);
  const [previewGPS, setPreviewGPS] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [rotate, setRotate] = useState(0);

  const totalRecords = tasks.length;
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const currentData = tasks.slice(startIndex, endIndex);

  return (
    <div className="mt-4 sm:mt-8">
      {/* 📱 Mobile: danh sách card */}
      <div className="lg:hidden space-y-3">
        {currentData.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-gray-200 bg-white px-4 py-12 text-center text-xs font-bold uppercase tracking-widest text-gray-400">
            Trống dữ liệu
          </div>
        ) : (
          currentData.map((t, i) => {
            const statusConfig = getStatusConfig(t.status);
            const fileCount = t.reports?.[0]?.files?.length || 0;
            const reportContent =
              t.reports?.[0]?.report_content || "Chưa có nội dung báo cáo";
            const rowNo = ((currentPage - 1) * pageSize + i + 1)
              .toString()
              .padStart(2, "0");

            return (
              <button
                key={t.id || `${t.employee_id}_${t.work_day}_${i}`}
                type="button"
                onClick={() => onSelect?.(t, "completed")}
                className="w-full text-left rounded-2xl border border-gray-100 bg-white p-4 shadow-sm transition active:scale-[0.99] active:bg-red-50/40"
              >
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-10 h-10 rounded-2xl bg-red-50 text-red-600 flex items-center justify-center text-[11px] font-black border border-red-100 shrink-0">
                      {getInitial(t.employee_name)}
                    </div>
                    <div className="min-w-0">
                      <p className="font-black text-slate-800 text-sm truncate">
                        {t.employee_name || "—"}
                      </p>
                      <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider truncate">
                        {t.branch_name || "Hệ thống"} · #{rowNo}
                      </p>
                    </div>
                  </div>
                  <span
                    className={`inline-flex items-center gap-1.5 shrink-0 px-2 py-1 rounded-full text-[9px] font-black border ${statusConfig.color}`}
                  >
                    <FontAwesomeIcon icon={statusConfig.icon} />
                    {statusConfig.text}
                  </span>
                </div>

                <div className="flex items-center gap-2 mb-2">
                  <span className="px-2.5 py-1 rounded-lg bg-slate-100 text-slate-600 font-black text-[10px]">
                    {formatDateUS(t.work_day)}
                  </span>
                  <div
                    className={`w-8 h-8 rounded-xl flex items-center justify-center font-black text-xs border ${getScoreClass(
                      t.judge_point
                    )}`}
                    title="Điểm"
                  >
                    {t.judge_point ?? "--"}
                  </div>
                  {fileCount > 0 && (
                    <span className="inline-flex items-center gap-1 text-[10px] font-bold text-sky-600 bg-sky-50 border border-sky-100 px-2 py-1 rounded-lg">
                      <FontAwesomeIcon icon={faPaperclip} />
                      {fileCount}
                    </span>
                  )}
                </div>

                <p className="font-semibold text-slate-800 text-sm leading-snug line-clamp-2">
                  {t.taskname}
                </p>
                {t.note && (
                  <p className="text-[11px] text-slate-400 mt-1 font-medium italic line-clamp-1">
                    📌 {t.note}
                  </p>
                )}

                <div className="mt-3 bg-blue-50/60 p-2.5 rounded-xl border border-blue-100/60">
                  <p className="text-xs font-semibold text-blue-700 italic line-clamp-2">
                    "{reportContent}"
                  </p>
                  {t.judge_content && (
                    <p className="mt-1.5 text-[10px] text-amber-700 font-bold line-clamp-2">
                      Sếp phản hồi: {t.judge_content}
                    </p>
                  )}
                </div>

                <div className="mt-3 flex items-center justify-end gap-1 text-[11px] font-bold text-red-600">
                  Xem chi tiết
                  <FontAwesomeIcon icon={faChevronRight} className="text-[9px]" />
                </div>
              </button>
            );
          })
        )}
      </div>

      {/* 🖥 Desktop: bảng dữ liệu */}
      <div className="hidden lg:block overflow-hidden rounded-3xl border border-gray-100 bg-white shadow-xl shadow-slate-200/50">
        <table className="min-w-full border-separate border-spacing-0">
          <thead className="bg-slate-50/80 backdrop-blur-sm">
            <tr>
              <th className="p-5 text-[11px] font-black text-slate-400 uppercase tracking-wider text-center w-[60px] border-b border-gray-100">#</th>
              <th className="p-5 text-[11px] font-black text-slate-400 uppercase tracking-wider text-center min-w-[140px] border-b border-gray-100">Ngày</th>
              <th className="p-5 text-[11px] font-black text-slate-400 uppercase tracking-wider text-left min-w-[160px] border-b border-gray-100">Nhân viên</th>
              <th className="p-5 text-[11px] font-black text-slate-400 uppercase tracking-wider text-left min-w-[400px] border-b border-gray-100">Nội dung công việc & Báo cáo</th>
              <th className="p-5 text-[11px] font-black text-slate-400 uppercase tracking-wider text-center min-w-[100px] border-b border-gray-100">Tài liệu</th>
              <th className="p-5 text-[11px] font-black text-slate-400 uppercase tracking-wider text-center min-w-[80px] border-b border-gray-100">Điểm</th>
              <th className="p-5 text-[11px] font-black text-slate-400 uppercase tracking-wider text-center min-w-[140px] border-b border-gray-100">Trạng thái</th>
            </tr>
          </thead>

          <tbody className="divide-y divide-gray-50">
            {currentData.length === 0 ? (
              <tr>
                <td colSpan={8} className="py-20 text-center text-gray-400 font-bold uppercase tracking-widest text-xs">Trống dữ liệu</td>
              </tr>
            ) : (
              currentData.map((t, i) => {
                const statusConfig = getStatusConfig(t.status);

                return (
                  <tr
                    key={t.id || i}
                    onClick={() => onSelect?.(t, "completed")}
                    className="group hover:bg-red-50/40 transition-all duration-300 cursor-pointer"
                  >
                    <td className="p-5 text-center font-black text-slate-300 text-xs">
                      {((currentPage - 1) * pageSize + i + 1).toString().padStart(2, "0")}
                    </td>

                    <td className="p-5 text-center">
                      <span className="px-3 py-1.5 rounded-lg bg-slate-100 text-slate-600 font-black text-[10px] tracking-tight">
                        {formatDateUS(t.work_day)}
                      </span>
                    </td>

                    <td className="p-5">
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-2xl bg-red-50 text-red-600 flex items-center justify-center text-[11px] font-black border border-red-100 shadow-sm shrink-0">
                          {getInitial(t.employee_name)}
                        </div>

                        <div className="flex flex-col min-w-0">
                          <span className="font-black text-slate-700 text-sm leading-tight truncate">
                            {t.employee_name || "—"}
                          </span>
                          <div className="flex items-center gap-1.5 mt-1">
                            <div className="w-1 h-1 bg-slate-300 rounded-full" />
                            <span className="text-[10px] font-bold text-slate-400 italic uppercase tracking-wider truncate">
                              {t.branch_name || "Hệ thống"}
                            </span>
                          </div>
                        </div>
                      </div>
                    </td>

                    <td className="p-5">
                      <div className="space-y-3">
                        <div>
                          <div className="font-semibold text-slate-800 text-sm leading-snug">{t.taskname}</div>
                          {t.note && (
                            <p className="text-[11px] text-slate-400 mt-0.5 font-medium italic">📌 {t.note}</p>
                          )}
                        </div>

                        <div className="bg-blue-50/50 p-3 rounded-2xl border border-blue-100/50">
                          <div className="text-sm font-semibold text-blue-700 italic">
                            "{(t.reports?.[0]?.report_content) || "Chưa có nội dung báo cáo"}"
                          </div>
                          {t.judge_content && (
                            <div className="mt-2 text-[11px] text-amber-700 font-black flex items-center gap-2">
                              <div className="w-1.5 h-1.5 bg-amber-500 rounded-full" />
                              SẾP PHẢN HỒI: <span className="font-bold italic">{t.judge_content}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </td>

                    <td className="p-5 text-center">
                      <div className="flex justify-center -space-x-2">
                        {t.reports?.[0]?.files?.slice(0, 3).map((f, idx) => (
                          <div key={idx} className="w-8 h-8 rounded-xl bg-white border-2 border-white shadow-sm flex items-center justify-center text-sky-600 group-hover:z-10 transition-all hover:-translate-y-1">
                            <FontAwesomeIcon icon={f.mime_type?.includes("pdf") ? faFilePdf : faImage} className="text-[10px]" />
                          </div>
                        ))}
                      </div>
                    </td>

                    <td className="p-5 text-center">
                      <div className={`w-9 h-9 mx-auto rounded-xl flex items-center justify-center font-black text-sm border ${getScoreClass(t.judge_point)}`}>
                        {t.judge_point ?? "--"}
                      </div>
                    </td>

                    <td className="p-5 text-center">
                      <span className={`inline-flex items-center gap-2 px-2 py-1.5 rounded-full text-[9px] font-black border shadow-sm ${statusConfig.color}`}>
                        <FontAwesomeIcon icon={statusConfig.icon} />
                        {statusConfig.text}
                      </span>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <Pagination
        totalRecords={totalRecords}
        currentPage={currentPage}
        pageSize={pageSize}
        onPageChange={setCurrentPage}
        onPageSizeChange={(size) => {
          setPageSize(size);
          setCurrentPage(1);
        }}
      />

      {/* ===========================
          📄 POPUP XEM PDF
      =========================== */}
      {previewPdf && (
        <div
          className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50"
          onClick={() => setPreviewPdf(null)}
        >
          <div
            className="bg-white rounded-xl w-[90%] h-[90%] shadow-lg relative"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Nút đóng lớn và dễ bấm */}
            <button
              className="absolute top-4 right-4 w-12 h-12 z-50 flex items-center justify-center
                        bg-black bg-opacity-40 text-white text-3xl rounded-full 
                        active:scale-95 transition"
              onClick={() => setPreviewPdf(null)}
              style={{ touchAction: "manipulation" }}
            >
              ✕
            </button>

            {/* PDF iframe */}
            <iframe
              src={previewPdf}
              className="w-full h-full rounded"
              title="PDF Preview"
            />
          </div>
        </div>
      )}



      {/* ===========================
          🖼 POPUP XEM ẢNH THƯỜNG
      =========================== */}
      {previewImage && (
        <div className="fixed inset-0 bg-black bg-opacity-80 z-50 flex items-center justify-center">

          {/* Nút đóng FIXED */}
          <button
            onClick={() => { setPreviewImage(null); setZoom(1); setRotate(0); }}
            className="fixed top-4 right-4 w-12 h-12 flex items-center justify-center 
                      bg-black bg-opacity-50 text-white text-3xl rounded-full z-[9999]
                      active:scale-95"
            style={{ touchAction: "manipulation" }}
          >
            ✕
          </button>

          {/* Toolbar FIXED - UI chuyên nghiệp */}
          <div
            className="
              fixed top-4 left-1/2 -translate-x-1/2 
              flex items-center gap-3 
              bg-white/20 backdrop-blur-md
              px-4 py-2 rounded-full 
              shadow-lg border border-white/30 
              z-[9999]
            "
          >
            <button onClick={() => setZoom(z => z + 0.2)} className="w-10 h-10 flex items-center justify-center">
              <ZoomIn size={22} className="text-white" />
            </button>

            <button onClick={() => setZoom(z => Math.max(0.4, z - 0.2))} className="w-10 h-10 flex items-center justify-center">
              <ZoomOut size={22} className="text-white" />
            </button>

            <button onClick={() => setRotate(r => (r + 90) % 360)} className="w-10 h-10 flex items-center justify-center">
              <RotateCcw size={22} className="text-white" />
            </button>

            <button onClick={() => { setZoom(1); setRotate(0); }} className="w-10 h-10 flex items-center justify-center">
              <RefreshCw size={22} className="text-white" />
            </button>
          </div>

          {/* Ảnh */}
          <div className="max-w-full max-h-full overflow-hidden flex items-center justify-center">
            <img
              src={previewImage}
              className="transition-transform"
              style={{
                transform: `scale(${zoom}) rotate(${rotate}deg)`,
                maxHeight: "90vh",
                maxWidth: "100vw",
              }}
            />
          </div>

        </div>
      )}



      {/* ===========================
          🗺 POPUP ẢNH + GPS
      =========================== */}
      {previewGPS && (
        <div className="fixed inset-0 bg-black bg-opacity-80 z-50 flex items-center justify-center">

          {/* Nút Đóng */}
          <button
            onClick={() => setPreviewGPS(null)}
            className="fixed top-4 right-4 w-12 h-12 flex items-center justify-center 
                      bg-black bg-opacity-50 text-white rounded-full shadow-lg
                      z-[9999]"
          >
            <X size={28} />
          </button>

          {/* Container chính */}
          <div
            className="bg-white rounded-xl w-[90%] max-h-[90vh] overflow-y-auto shadow-lg p-4 
                      flex flex-col lg:flex-row gap-4"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Ảnh */}
            <div className="w-full lg:w-1/2 flex justify-center">
              <img
                src={previewGPS.file_url}
                alt="preview-gps"
                className="w-full object-contain rounded-lg"
                style={{ maxHeight: "60vh" }}
              />
            </div>

            {/* Map */}
            <div className="w-full lg:w-1/2">
              <iframe
                width="100%"
                height="100%"
                className="min-h-[250px] rounded-xl"
                style={{ border: 0 }}
                loading="lazy"
                allowFullScreen
                referrerPolicy="no-referrer-when-downgrade"
                src={`https://www.google.com/maps?q=${previewGPS.lat},${previewGPS.lng}&z=15&output=embed`}
              ></iframe>
            </div>
          </div>

        </div>
      )}



    </div>
  );
};

export default TaskMainTable;